import { useMemo } from 'react';
import { useQueries } from 'react-query';
import isEmpty from 'lodash/isEmpty';
import HouseholdProfileService from '../../services/householdProfileService';
// eslint-disable-next-line max-len
import { makeHouseholdProfileAffordableQualificationsByProgramQueryKey } from '../../containers/GeneralAffordable/hooks';

// Mirrors the backend's own reopen predicate (Applications.js#updateApplicants,
// `aqhs.some((aqh) => !aqh.isActive)`) rather than isCompletedCertification, which also
// requires certificationStatus === COMPLETE. A cert that was reopened and then completed
// again can sit under the Completed tab with certificationStatus still REOPEN -- isActive
// is the only field the backend actually keys the reopen off of.
const willReopenCertification = (cert) => !cert?.isActive;

/**
 * Whether a household-composition change would reopen any of the household's
 * affordable programs' certifications, read via the same per-program query
 * (getHouseholdProfileAffordableQualificationsByProgram) the LIHTC/HUD/RD/PH
 * Completed tab itself renders from -- so this can never disagree with what
 * "Completed (N)" shows on screen the way the old
 * applicationProfile.affordableQualificationHistories read did (that field is
 * never populated by GET_ONE_APPLICATION_SUCCESS's payload).
 *
 * Shares its query key with that same per-program query, so mounting this
 * alongside an open Completed tab costs nothing extra -- react-query
 * dedupes the requests.
 *
 * enabled must stay false on every resident-path caller: the resident flow
 * has no equivalent warning today, and this hook exists only for the
 * applicant-path Change Head of Household / Mini-Profile Add flows that do.
 */
const useHouseholdWillReopenCertification = ({
  organizationId,
  propertyId,
  householdId,
  propertyAffordableProgramIds = [],
  enabled = false,
}: {
  organizationId?: string,
  propertyId?: string,
  householdId?: string,
  propertyAffordableProgramIds?: Array<string>,
  enabled?: boolean,
}) => {
  const householdProfileService = useMemo(
    () => new HouseholdProfileService(),
    [],
  );

  const programIds = (propertyAffordableProgramIds || []).filter(Boolean);
  const canQuery =
    enabled &&
    !isEmpty(organizationId ?? '') &&
    !isEmpty(propertyId ?? '') &&
    !isEmpty(householdId ?? '') &&
    programIds.length > 0;

  const queries = useQueries(
    (canQuery ? programIds : []).map((propertyAffordableProgramId) => ({
      queryKey: makeHouseholdProfileAffordableQualificationsByProgramQueryKey({
        organizationId,
        propertyId,
        householdId,
        propertyAffordableProgramId,
      }),
      queryFn: () =>
        householdProfileService.getHouseholdProfileAffordableQualificationsByProgram(
          organizationId,
          propertyId,
          householdId,
          propertyAffordableProgramId,
        ),
      enabled: canQuery,
      refetchOnWindowFocus: false,
    })),
  );

  // Not memoised: useQueries hands back a fresh array every render, so a memo
  // keyed on it would never hit. The scan is a couple of `some` calls.
  return queries.some((query) =>
    (query.data?.affordableQualifications ?? []).some((row) =>
      willReopenCertification(row?.qualificationData),
    ),
  );
};

export default useHouseholdWillReopenCertification;
